import { deburr, escapeRegExp } from "lodash";
import React from "react";
import { NBSP, RE_PLEX_ID } from "../lib/utils";

export const MARKDOWN_CHARS = "[[\\]`#*_]";
export const RE_MD_CHARS_ESCAPED = new RegExp(`\\\\(${MARKDOWN_CHARS})`, 'g');

// Misc utilities with no better home ATM
// WARNING: must NOT change the length of the text
export const normalize = text => (
  //text.normalize("NFD").replace(/[\u0300-\u036f]/g, "")
  deburr(text)
);

export const hasMark = (element) => {
  return !!(element?.querySelector('mark'));
};

export const markMatchingText = (withinText, filter, params = {}) => {
  const {asString = false, attrs = {}} = params;
  // Check the full filter, as well as individual words within it
  // Also check word blocks that exclude the final word
  if (!filter || !withinText || typeof (withinText) !== "string") {
    return withinText;
  }
  const options = [normalize(filter)];
  const words = options[0].trim().split(/\s/).filter(el => el && el.length > 2);
  if (words.length > 1) {
    options.push(words.slice(0, -1).join(" "));
  }
  options.push(...words);
  const searchPhrases = Array.from(new Set(options))
    .sort((a, b) => b.length - a.length);

  const protectedLinks = {};
  let idx = 0;
  const sansLinks = withinText
     // Protect markdown links
    .replace(/(^\[[^\]]+]:.*$|((?<=\[[^\]]+])(\[[^\]]+]|\([^)]+\))))/mg, expr => {
      const key = `____${++idx}____`;
      protectedLinks[key] = expr;
      return key;
    })
    // Protect bare URLs (http/https)
    .replace(/https?:\/\/[^\s\])"]+/g, expr => {
      const key = `____${++idx}____`;
      protectedLinks[key] = expr;
      return key;
    })
    // Protect br tags
    .replace(/(<br[^>]*\/?>)/gi, expr => {
      const key = `____${++idx}____`;
      protectedLinks[key] = expr;
      return key;
    });
  //console.log("With links protected", escapedText);
  const markPhrase = (phrase, inText, keyBase = "key") => {
    // Match any non-word characters in the input phrase, and allow for extra dash or space preceding digits (i.e.
    // allow "MEK2" to match "MEK-2" or "MEK 2")
    const re_phrase = looseMatchRegExp(phrase);
    const output = [];
    const normalized = normalize(inText);
    let offset = 0;
    let match;
    while ((match = re_phrase.exec(normalized)) !== null) {
      if (match.index > offset) {
        const prefix = inText.substring(offset, match.index);
        output.push(prefix);
      }
      const matchedText = inText.substring(match.index, match.index + match[0].length);
      output.push(mark(matchedText, {asString, attrs: {key: `${keyBase}-${++idx}`}}));
      offset = re_phrase.lastIndex;
    }
    if (offset < inText.length) {
      output.push(inText.substring(offset));
    }
    return output;
  }
  const marked = [sansLinks];
  searchPhrases.forEach((phrase, idx) => {
    const updated = [];
    marked.forEach(value => {
      if (typeof (value) !== "string" || value.startsWith("<mark")) {
        // Already marked, leave it alone
        updated.push(value);
      } else if (value) {
        updated.push(...markPhrase(phrase, value, `${phrase.replace(/\W+/g,"-")}-${idx}`));
      }
    });
    marked.splice(0, updated.length, ...updated);
  });
  const withRestoredLinks = marked.flatMap(el => {
    if (typeof (el) !== "string") {
      return el;
    }
    // Restore protected content and split on br tags to convert them to React elements
    const restored = el.replace(/____\d+____/mg, expr => protectedLinks[expr]);
    // A string result keeps its br tags as text: joined, a React <br> reads "[object Object]"
    if (!restored.includes('<br') || asString) {
      return restored;
    }
    // Split on br tags and convert them to React elements
    const parts = restored.split(/(<br[^>]*\/?>)/gi);
    return parts.map((part, i) => {
      if (/<br[^>]*\/?>/i.test(part)) {
        return <br key={`br-${idx++}`} />;
      }
      return part || null;
    }).filter(x => x !== null && x !== '');
  });
  //console.log("Output elements", withRestoredLinks);
  return asString ? withRestoredLinks.join(
    "") : withRestoredLinks.length === 1 ? withRestoredLinks[0] : withRestoredLinks;
}

export const mark = (text, {asString = false, attrs = {}}) => {
  const {key, ...props} = attrs;
  if (asString) {
    const sattrs = Object.entries(props).reduce((result, [k, v]) => (
      `${result} ${k}="${v}"`
    ), "");
    return `<mark${sattrs}>${text}</mark>`;
  }
  return (<mark key={key} {...props}>{text}</mark>);
};

// Cf python highlight matching in autosuggest.py
// Ensure "ICG001" will match "ICG-001" or "ICG:001" or "ICG 001" and vice versa
export const looseMatchRegExp = (phrase, flags = 'giu') => {
  const escaped = escapeRegExp(phrase
                                 .replace(/\s+/g, "ssSPACEss")
                                 .replace(/[\W_]/gu, "__DOT__"));
  const simplified = escaped
    .replace(/ssSPACEss/gu, "\\s")
    .replace(/__DOT__(__DOT__)*/gu, "((?![\n*])[\\W_])?");
  const relaxed = simplified
    .replace(/(?<=[^\d\s\W])(\d)/gu, "[-:\\s]?$1");
  return new RegExp(relaxed, flags);
};

const hideMarkdownChars = s => {
  const modified = s.replace(new RegExp(`((?<!\\\\)${MARKDOWN_CHARS})`, "gs"), "---\\$1---");
  return modified === s ? s : modified;
};
const restoreMarkdownChars = s => {
  const modified = s.replace(new RegExp(`---\\\\(${MARKDOWN_CHARS})---`, "gs"), "$1");
  return modified === s ? s : modified;
};

// Patch up any markdown which may have inadvertent escapes or other problematic patterns
// A link's target without its optional title: (bdb:1278236 "bdb:1278236") -> bdb:1278236.
// interpolateSourceLinks adds the title; tested as one string, a Plex id followed by a title
// isn't a valid id, so the link's brackets were escaped and it showed as raw Markdown (PLEX-6676).
// An id's internal pipes are read back from &#124;, which the step before encodes them as: RE_PLEX_ID
// rejects the entity's ";", so a link like [VAMP7](biogrid:112712|9606|b|p) showed as raw Markdown
// (PLEX-6692).
// A title may hold the other kind of quote ("FRET Z'-Lyte"): only its own closing quote ends it.
const linkTarget = url => url.split('|')[0].trim().replace(/\s+("[^"]*"|'[^']*')$/, '').replace(/&#124;/g, '|');

export const prepareMarkdown = s => {
  // ESCAPE_THIS {NOT_THIS}
  // [ESCAPE_THIS][NOT_THIS]
  // [ESCAPE_THIS](NOT_THIS)
  // (ESCAPE_THIS) (https://NOT_THIS)
  // FIXME is this really necessary with a proper escapeMD and usage?
  // FIXME probably doesn't need any interpolation-related stuff, since these rules are applied post-interpolation
  {
    let modified = s
      // Convert reference-style multi-links [TEXT][URL1|URL2] to inline [TEXT](URL1|URL2)
      // LLMs sometimes output this format; the second bracket is not a valid MD reference
      // label (no matching definition), so convert to the inline pipe-separated URL format
      // that SourcesLink understands. Only convert when the bracket contains a pipe character.
      .replace(/(?<!\])\[([^\]]+)\]\[([^\]]*\|[^\]]*)\]/g, '[$1]($2)')
      // Convert legacy numbered citations [[1]](url) to standard [1](url)
      // This handles old conversations that used the [[N]](url) format for citations
      .replace(/\[\[(\d+)\]\]\(([^)]+)\)/g, '[$1]($2)')
      // Convert LLM internal XML-like tags to styled spans to preserve info without breaking markdown
      // This must happen BEFORE underscore escaping to avoid creating invalid tag names like "function\_calls"
      .replace(/<(\/?)([a-z_]+:[a-z_]+|function_calls|function_results|thinking|antml:thinking|antml:invoke|antml:parameter)(?:\s[^>]*)?\s*>/gi,
        (match, slash, tagName) => {
          if (slash) {
            // Closing tag
            return '</span>';
          } else {
            // Opening tag - convert tag name to valid CSS class (replace colons and underscores with hyphens)
            const className = tagName.replace(/[_:]/g, '-');
            return `<span class="llm-tag llm-tag-${className}">`;
          }
        })
      // Based on extant markup from interpro data
      // Convert <br> tags to newlines, but preserve <br class='newline' /> which carries semantic info
      .replace(/<br(?!\s+class=['\"]newline['\"])[^>]*>/gi, "\n")
      .replace(/\s*<ul>([^\S\n]*\n|$)/gi, "\n")
      .replace(/\s*<\/ul>([^\S\n]*\n|$)/gi, "\n\n")
      // Fix up standalone URLs (w/parens)
      .replace(/(?<!])(\((https?:\/\/[^)]+?\/)([^\/)]+)\))/gi, (match, parents, url, page) => {
        return `([${page.replace(/\\?_/, " ")}](${url}${page}))`;
      })
      // Escape underscores where we can be pretty sure they're not markdown
      .replace(/(?<=[A-Za-z0-9])(?!interpolated)_(?!inline)(?=[A-Za-z0-9])/g, "\\_")
      // Preserve space after the mark and preceding a previously-existing span
      .replace(/^(<mark.*?<\/mark>) +(?=<span[^>]*>)/gm, `$1${NBSP}`)
      // Markdown will convert "^<mark>.*</mark>(.*)" into "<span><mark>.*</mark><p>$1</p></span>", which introduces
      // a line break after the marked text.  Wrap the trailing raw text in a span to avoid the line break.
      // This is only an issue when <mark> is at the beginning of line
      .replace(/^(<mark[^>]*>[^<]+<\/mark>(?!<span))(.+)$/gm, "$1<span class='prepared'>$2</span>")
      // Preserve any space at the beginning of an inserted span directly after marked text (only if at BOL)
      // Avoid stomping on <ul>/<ol>
      .replace(/^(<mark.*?<\/mark><span[^>]*>)? (?!$|[*\d-])/gm, `$1${NBSP}`)
      // Preserve double-newline after </span>, but only when line starts with <mark>
      .replace(/^(<mark.*?<\/span>)( {2}|\n)\n/gm, "$1<p/>")
      // Escape underscores around marked text
      .replace(/((?<=<\/mark>)_|_(?=<mark))/g, "\\_")
      // No escape within interpolation braces (limit backtracking by requiring \\_)
      .replace(/(?<={)([^}]*?\\_[^}]*)(?=})/gs, m => m.replace(/\\_/g, "_"))
      // No escape within hyperlink link definition
      .replace(/(?<!\\)(?<=]\()([^)]*?\\_[^)]*)(?=\))/gs, m => m.replace(/\\_/g, "_"))
      // No escape within reference link definition
      .replace(/(?<!\\)(?<=]\[)(\s*[^\]]*?\\_[^\]]*)(?=])/gs, m => m.replace(/\\_/g, "_"))
      // No escape between <code> tags (between backticks)
      .replace(/(?<=`)([^`]*?\\_[^`]*)(?=`)/gs, m => m.replace(/\\_/g, "_"))
      // No escape in attribute values (double-quoted strings)
      .replace(/(?<==")([^"]*?\\_[^"]*)(?=")/gs, m => m.replace(/\\_/g, "_"))
      // Fix up ul/ol
      .replace(/\n<[uo]l>\n/gi, "\n")
      .replace(/\n<\/[uo]l>\n/gi, "\n\n")
      // Preserve all markdown characters within angle brackets
      .replace(/(<[^>]+>)/gs, match => hideMarkdownChars(match))
      // Encode internal pipes within single Plex ID link URLs as &#124; to prevent
      // markdown table parsers from treating them as column separators.
      // e.g. [VAMP7](biogrid:112712|9606|b|p) → [VAMP7](biogrid:112712&#124;9606&#124;b&#124;p)
      // Only encodes pipes where the segment after is NOT a valid Plex ID or http URL,
      // preserving legitimate multi-URL syntax [text](plexid1|plexid2).
      // The HTML entity &#124; is decoded back to | by the browser before React sees the href.
      .replace(/(?<=[^\\])\[[^\]]*(?<!\\)]\(([^)]+)\)/g, (match, url) => {
        if (!url.includes('|')) return match;
        const parts = url.split('|');
        let hasInternalPipes = false;
        const encoded = parts.reduce((result, segment, i) => {
          if (i === 0) return segment;
          const trimmed = segment.trim();
          const isSeparator = RE_PLEX_ID.test(trimmed) || /^https?:/.test(trimmed);
          if (!isSeparator) hasInternalPipes = true;
          return result + (isSeparator ? '|' : '&#124;') + segment;
        }, '');
        if (!hasInternalPipes) return match;
        return match.replace(/\(([^)]+)\)$/, `(${encoded})`);
      })
      // Escape "[M](N)" patterns that are probably not links (e.g. IUPAC names)
      // e.g. dioxooctahydro-1h-pyrazino[1,2-a](6s,9as)-n-benzyl
      // For pipe-separated multi-URLs, validate only the first segment
      .replace(/(?<=[^\\])\[[^\]]*(?<!\\)]\(([^)]+)\)/g,
               (match, url) => {
                 const firstUrl = linkTarget(url);
                 return /^(#|https?:|ftp:|ftps:|mailto:|tel:|sms:|geo:|\/\/)/.test(firstUrl) || RE_PLEX_ID.test(firstUrl)
                   ? match : match.replace(/([[\]])/g, "\\$1");
               })
      // Catch malformed LLM patterns like [text:</tag>](text:</tag>) or [text](tag:value)>
      // These have XML-like tag syntax in URLs or stray > after closing paren
      .replace(/\[([^\]]+)\]\(([^)]*(?::[^)]*|<[^)]*>))\)>/g, (match, text, url) => {
        // Escape if URL contains tag-like syntax (colon/angle brackets) and isn't a valid link
        if (!/^(#|https?:|[a-z]+gene:|gwps:|gpsa:|decrypte:|chem:)/.test(url)) {
          return match.replace(/([[\]])/g, "\\$1");
        }
        return match;
      })
    // Escape brackets within MD link text (e.g. [IUPAC](URL))
    // Use simpler non-recursive pattern to avoid catastrophic backtracking
    modified = modified.replace(/(?<!\\)\[([^\[\]\\]+(?:\\.[^\[\]\\]*)*)\](?<!\\)\(([^()\\]+)\)/gm,
                                (match, label, url) => {
                                  const firstUrl = linkTarget(url);
                                  if (/^(#|https?:|ftp:|ftps:|mailto:|tel:|sms:|geo:|\/\/)/.test(firstUrl) || RE_PLEX_ID.test(firstUrl)) {
                                    return `[${label.replace(/(?<!\\)([\[\]])/gm, "\\$1")}](${url})`;
                                  }
                                  return match.replace(/([\[\]])/gm, "\\$1");
                                })
    return restoreMarkdownChars(modified === s ? s : modified);
  }
};

export const escapeMD = (obj) => {
  if (obj.map) {
    return obj.map(x => escapeMD(x));
  }
  const s = obj.toString()
    // Preserve any MD chars within html markup
    .replace(/(<[^>]+>)/gs, match => hideMarkdownChars(match))
    // Escape any MD chars not already escaped
    .replace(new RegExp(`(?<!\\\\)(${MARKDOWN_CHARS})`, "gs"), "\\$1");
  return restoreMarkdownChars(s);
};

export const unescapeMD = (obj) => {
  const sobj = obj.toString();
  return sobj.replace(RE_MD_CHARS_ESCAPED, "$1");
};


export const getObjectProjection = (obj = {}, fields = []) =>
  fields.length === 0
    ? obj
    : fields.reduce(
        (output, fieldName) =>
          Object.assign(output, { [fieldName]: obj[fieldName] }),
        {}
      );

export const createSample = (name, template, data) => ({
  name: name,
  template: template,
  dataObject: data
});

